import {AfterViewInit, Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
    AppNameEnum,
    ConverterOutput,
    EricResponse,
    FrontendStatusEnum,
    NachdigalService,
    Status,
    StatusEnum
} from '@nachdigal';
import {
    ComponentHelper,
    EricErrorNavService,
    EricErrorRoute,
    EricService,
    FragebogenWrapper,
    NavigationService,
    PreviewDocumentService,
    UpdateDocumentService,
    WizardPageBaseComponent
} from '@utils-lib';
import {DatevNotificationService, DatevNotificationSeverity} from '@datev/ngx-ui-components';
import {TranslateService} from '@ngx-translate/core';
import {LoaderService} from '../../service/loader.service';
import {NavigationProviderService} from '../../service';
import {environment} from '../../../../environments/environment';
import {PDF_VIEWER_PATH} from '../../../pdf-viewer/pdf-viewer.routing.module';
import {AttachmentService} from '../../service/attachment.service';
import {finalize, first, tap} from 'rxjs/operators';

@Component({
    selector: 'common-ui-antrag-versenden',
    templateUrl: './antrag-versenden.component.html',
    styleUrls: ['./antrag-versenden.component.scss']
})
export class AntragVersendenComponent extends WizardPageBaseComponent<FragebogenWrapper> implements OnInit, AfterViewInit, OnDestroy {

    static nutzdatenPath = undefined;
    static route = 'antrag-versenden';
    taskId?: string;
    public ericResponse: EricResponse;
    public sendPending = false;
    public backButtonRoute: string;
    public readonly FINANZAMT_MISSING_RESPONSE: EricResponse = {
        result: -1,
        result_string: this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANTRAG_VERSENDEN.FINANZAMT_FEHLT'),
        files: []
    };
    public hasAttachments = false;
    private statusInterval;
    private converterOutput: ConverterOutput;

    private readonly appEnum: AppNameEnum;

    private readonly documentService: NachdigalService;
    private readonly ericService: EricService;
    private readonly ericErrorNavService: EricErrorNavService;
    private readonly router: Router;
    private readonly notificationService: DatevNotificationService;
    private readonly previewService: PreviewDocumentService;
    private readonly navigationService: NavigationService;
    private readonly navigationProvider: NavigationProviderService;
    private readonly updateDocumentService: UpdateDocumentService;
    private readonly loadingService: LoaderService;

    constructor(
        protected readonly activatedRoute: ActivatedRoute,
        private readonly translateService: TranslateService,
        private readonly attachmentService: AttachmentService,
        private readonly injector: Injector) {

        super(activatedRoute);

        this.loadingService = injector.get<LoaderService>(LoaderService);
        this.loadingService.isLoading = {isLoading: false};

        this.router = injector.get<Router>(Router);
        this.documentService = injector.get<NachdigalService>(NachdigalService);
        this.ericService = injector.get<EricService>(EricService);
        this.ericErrorNavService = injector.get<EricErrorNavService>(EricErrorNavService);
        this.notificationService = injector.get<DatevNotificationService>(DatevNotificationService);
        this.translateService = injector.get<TranslateService>(TranslateService);
        this.previewService = injector.get<PreviewDocumentService>(PreviewDocumentService);
        this.navigationService = injector.get<NavigationService>(NavigationService);
        this.navigationProvider = injector.get<NavigationProviderService>(NavigationProviderService);
        this.updateDocumentService = injector.get<UpdateDocumentService>(UpdateDocumentService);

        const navItems = this.navigationProvider.navigationPages;
        this.backButtonRoute = `../${navItems[navItems.length - 2].route}`;
        this.appEnum = ComponentHelper.getAppEnum(this.activatedRoute.snapshot);
        const status = this.activatedRoute.snapshot.parent.data.document.status;
        if (status === StatusEnum.SendingConfirmed || status === StatusEnum.Sending) {
            this.sendPending = true;
            this.navigationService.changeDisableStatus(this.sendPending);
        }
    }

    public ngOnInit(): void {
        super.ngOnInit();
        clearInterval(this.statusInterval);
        this.converterOutput = this.activatedRoute.snapshot.data.converterOutput;
        this.check();
        this.frontendDocument.status = this.activatedRoute.snapshot.parent.data.document.status;
        if (this.frontendDocument.status === FrontendStatusEnum.SendingConfirmed) {
            this.getStatus();
        }

        this.taskId = this.activatedRoute.snapshot.parent.paramMap.get('taskId');
        if(this.taskId){
          this.attachmentService.load(this.taskId).pipe(
            first(),
            tap((datadata)=>{
              this.hasAttachments = datadata.length > 0;
            }),
            finalize(() => {})
          ).subscribe({
            next: null,
            error: () => {}
          });
        }

    }

    public ngAfterViewInit(): void {
        window.dispatchEvent(new Event('resize'));
    }

    public ngOnDestroy(): void {
        clearInterval(this.statusInterval);
    }

    public toOverview(): void {
        this.sendPending = false;
        this.router.navigate([environment.redirectPath]);
    }

    public onSending(){
      console.log('Sending...');
    }

    public async send(): Promise<void> {
        this.sendPending = true;
        this.navigationService.changeDisableStatus(this.sendPending);
        this.notificationService.close('sendError');

        // dieser Antrag beantragt eine neue Steuernummer, da NIGHT-INGATE aber unbedingt eine haben will... ;)
        const steuernummer = '0';
        let antragsteller = '';
        let finanzamtnummer = -1;

        if (this.data.aufnahmeGewerblicheTaetigkeit) {
            antragsteller = `${this.data.aufnahmeGewerblicheTaetigkeit.personenAngaben.antragsteller.nachname}, ` +
                `${this.data.aufnahmeGewerblicheTaetigkeit.personenAngaben.antragsteller.vorname}`;
            finanzamtnummer = this.data.aufnahmeGewerblicheTaetigkeit.finanzamtAngaben.finanzamt.finanzamtnummer;
        }
        if (this.data.gruendungKapitalgesellschaft) {
            antragsteller = this.data.gruendungKapitalgesellschaft.allgKapGesAngaben.juristischePerson.firmenname;
            finanzamtnummer = this.data.gruendungKapitalgesellschaft.finanzamtAngaben.finanzamt.finanzamtnummer;
        }
        if (this.data.gruendungPersonengesellschaft) {
            antragsteller = this.data.gruendungPersonengesellschaft.angabenZumUnternehmen.juristischePerson.firmenname;
            finanzamtnummer = this.data.gruendungPersonengesellschaft.finanzamtAngaben.finanzamt.finanzamtnummer;
        }
        if (this.data.beteiligungPersonengesellschaft) {
            antragsteller = `${this.data.beteiligungPersonengesellschaft.personenAngaben.antragsteller.nachname}, ` +
                `${this.data.beteiligungPersonengesellschaft.personenAngaben.antragsteller.vorname}`;
            finanzamtnummer = this.data.beteiligungPersonengesellschaft.finanzamtAngaben.finanzamt.finanzamtnummer;
        }
        if (this.data.koerperschaftNachAuslaendischemRecht) {
            antragsteller = this.data.koerperschaftNachAuslaendischemRecht.korperschaftAngaben.juristischePerson.firmenname;
            finanzamtnummer = this.data.koerperschaftNachAuslaendischemRecht.finanzamtAngaben.finanzamt.finanzamtnummer;
        }

        this.ericService
            .send(this.frontendDocument.id, this.converterOutput, steuernummer, antragsteller, finanzamtnummer)
            .subscribe(
                (status: Status) => {
                    this.frontendDocument.status = status.status;
                    if (this.frontendDocument.status === FrontendStatusEnum.SendingConfirmed) {
                        this.getStatus();
                    } else {
                        this.frontendDocument.status = status.status;
                        this.frontendDocument.transfer_date = status.transfer_date;
                        this.updateDocumentService.changeDocument(this.frontendDocument);
                        this.navigationService.changeDisableStatus(this.sendPending);
                    }
                },
                () => {
                    this.sendPending = false;
                    this.notificationService.showAlert(
                        DatevNotificationSeverity.Error,
                        this.translateService.instant('COMMON_UI.NOTIFICATIONS.SEND_ERROR.TITEL'),
                        null,
                        this.translateService.instant('COMMON_UI.NOTIFICATIONS.SEND_ERROR.MESSAGE'),
                        [{id: 'sendErrorOk', label: 'OK'}], 'sendError');
                }
            );
    }

    public handleEricErrorResponse(errorMessage): void {
        // TODO ERIC RESPONSE PROPOSAL. PLEASE DO NOT DELETE THE FOLLOWING LINES
        // this.navigateToView(errorMessage.id);
        if (this.appEnum === AppNameEnum.FragebogenEUn) {
            this.ericErrorNavService.setEricErrorMessage(errorMessage);
            this.ericErrorNavService.setEricErrorNavStatus(true);
            this.ericErrorNavService.setEricErrorCard(errorMessage);
            const route: EricErrorRoute = this.ericErrorNavService.getEricErrorRouteConfig();
            if (route) {
                this.router.navigate(['../', route.path], {
                    queryParams: route.queryParams,
                    relativeTo: this.activatedRoute
                });
            }
        }
    }

    public displayPreview(): void {
        this.router.navigate([PDF_VIEWER_PATH,this.activatedRoute.snapshot.root.firstChild?.routeConfig.path, this.frontendDocument.id]);
    }

    protected getData(): FragebogenWrapper {
        if (this.appEnum === AppNameEnum.FragebogenEUn) {
            return {aufnahmeGewerblicheTaetigkeit: this.activatedRoute.snapshot.data.data};
        }
        if (this.appEnum === AppNameEnum.FragebogenKapG) {
            return {gruendungKapitalgesellschaft: this.activatedRoute.snapshot.data.data};
        }
        if (this.appEnum === AppNameEnum.FragebogenPersG) {
            return {gruendungPersonengesellschaft: this.activatedRoute.snapshot.data.data};
        }
        if (this.appEnum === AppNameEnum.FragebogenBetPG) {
            return {beteiligungPersonengesellschaft: this.activatedRoute.snapshot.data.data};
        }
        if (this.appEnum === AppNameEnum.FragebogenKapGAus) {
            return {koerperschaftNachAuslaendischemRecht: this.activatedRoute.snapshot.data.data};
        }
        throw new Error('Error in getData(): could not detect the AppEnumName');
    }

    protected getStammdatenMappingRules(): Map<string, Array<unknown>> {
        return new Map<string, Array<unknown>>([]);
    }

    private check(): void {
        if (this.readonly) {
            return;
        }

        this.notificationService.close('checkError');

        // Sonderbehandlung, da ERIC Plausiprüfung bei fehlender Finanzamtnummer keine sprechende Fehlermeldung liefert
        if (!this.finanznummerExist()) {
            this.ericResponse = this.FINANZAMT_MISSING_RESPONSE;
            return;
        }

        this.ericService.check(this.frontendDocument.id, this.converterOutput).subscribe((ericResponse: EricResponse) => {
            this.ericResponse = ericResponse;
        }, () => {
            this.notificationService.showAlert(DatevNotificationSeverity.Error,
                this.translateService.instant('COMMON_UI.NOTIFICATIONS.CHECK_ERROR.TITEL'),
                null,
                this.translateService.instant('COMMON_UI.NOTIFICATIONS.CHECK_ERROR.MESSAGE'),
                [{id: 'plausiCheckErrorOK', label: 'OK'}],
                'checkError');
        });
    }

    private finanznummerExist(): boolean {
        let finanznummerDoesNotExist = !!this.data.aufnahmeGewerblicheTaetigkeit &&
            !this.data.aufnahmeGewerblicheTaetigkeit.finanzamtAngaben?.finanzamt?.finanzamtnummer;

        finanznummerDoesNotExist = finanznummerDoesNotExist || (!!this.data.gruendungKapitalgesellschaft &&
            !this.data.gruendungKapitalgesellschaft.finanzamtAngaben?.finanzamt?.finanzamtnummer);

        finanznummerDoesNotExist = finanznummerDoesNotExist || (!!this.data.gruendungPersonengesellschaft &&
            !this.data.gruendungPersonengesellschaft.finanzamtAngaben?.finanzamt?.finanzamtnummer);

        finanznummerDoesNotExist = finanznummerDoesNotExist || (!!this.data.beteiligungPersonengesellschaft &&
            !this.data.beteiligungPersonengesellschaft.finanzamtAngaben?.finanzamt?.finanzamtnummer);

        finanznummerDoesNotExist = finanznummerDoesNotExist || (!!this.data.koerperschaftNachAuslaendischemRecht &&
            !this.data.koerperschaftNachAuslaendischemRecht.finanzamtAngaben?.finanzamt?.finanzamtnummer);

        return !finanznummerDoesNotExist;
    }

    private getStatus(): void {
        const interval = 2000;
        this.statusInterval = setInterval(() => {
            this.documentService.getTaskStatus(this.frontendDocument.id).toPromise().then(status => {
                if (status.status !== StatusEnum.SendingConfirmed) {
                    clearInterval(this.statusInterval);
                    this.sendPending = false;
                    this.frontendDocument.status = status.status;
                    this.frontendDocument.transfer_date = status.transfer_date;
                    this.updateDocumentService.changeDocument(this.frontendDocument);
                    this.navigationService.changeDisableStatus(this.sendPending);
                    if (!this.ericResponse) {
                        this.ericResponse = {result: 0};
                    }
                }
            });
        }, interval);
    }

    // TODO ERIC RESPONSE PROPOSAL. PLEASE DO NOT DELETE THE FOLLOWING LINES
    private navigateToView(id: string): void {
        for (const navItem of this.navigationProvider.navigationPages) {
            if (navItem.erricFailureIds && navItem.erricFailureIds.includes(id)) {
                this.router.navigate(['../' + navItem.route], {
                    state: {dataEricId: id},
                    relativeTo: this.activatedRoute
                });
            }
        }
    }

}
